import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Download, FileArchive, FileSpreadsheet, FileText } from 'lucide-react'

import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { TAG_FORMATS, type TagContext, type TagCreative, type TagFormat } from '@/services/creatives/creativeTags'
import {
  downloadCreativeTags,
  type DownloadFormat,
  type TagDownloadContext,
} from '@/services/creatives/creativeTagDownloads'

interface CreativeTagDownloadMenuProps {
  /** The creatives to write tags for — usually the ones currently listed. */
  creatives: TagCreative[]
  /** What the tags request: the serving parent and the network's tag ID. */
  tagContext: TagContext
  /** The objects the trafficking spreadsheets describe. */
  downloadContext: TagDownloadContext
}

/**
 * Downloads the third-party ad tags for a set of creatives, in the chosen
 * format, as zipped text files, zipped HTML files, one text file, or a
 * trafficking spreadsheet where the format has one.
 */
export function CreativeTagDownloadMenu({
  creatives,
  tagContext,
  downloadContext,
}: CreativeTagDownloadMenuProps) {
  const { t } = useTranslation()
  const [format, setFormat] = useState<TagFormat>('STANDARD_JAVASCRIPT')
  const [busy, setBusy] = useState(false)

  const selectedFormat = TAG_FORMATS.find((f) => f.id === format) ?? TAG_FORMATS[0]

  const download = async (downloadFormat: DownloadFormat) => {
    setBusy(true)
    try {
      await downloadCreativeTags(downloadFormat, format, tagContext, creatives, downloadContext)
    } catch {
      toast.error(t('creatives.tagDownloadFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={creatives.length === 0} loading={busy}>
          <Download className="size-4" /> {t('creatives.downloadTags')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t('creatives.tagFormat')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={format} onValueChange={(v) => setFormat(v as TagFormat)}>
          {TAG_FORMATS.map((f) => (
            // Picking a format only sets up the download, so keep the menu open for it.
            <DropdownMenuRadioItem key={f.id} value={f.id} onSelect={(e) => e.preventDefault()}>
              {f.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('creatives.downloadOptions')}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => download('ZIPPED_TXT')}>
          <FileArchive className="size-4" /> {t('creatives.downloadZippedTxt')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => download('ZIPPED_HTML')}>
          <FileArchive className="size-4" /> {t('creatives.downloadZippedHtml')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => download('TXT')}>
          <FileText className="size-4" /> {t('creatives.downloadSingleTxt')}
        </DropdownMenuItem>
        {selectedFormat.xls && (
          <DropdownMenuItem onSelect={() => download('XLS')}>
            <FileSpreadsheet className="size-4" /> {t('creatives.downloadSingleXls')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
